import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Paper, Select, Stack } from '@mui/material';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useBranch from '../../hooks/useBranch';
import useEntertainment from '../../hooks/useEntertainment';
import DatePicker from '../Bookings/CreateBookingsModal/components/components/DatePicker';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const CleanupContext = createContext(null);

export const useCleanupContext = () => {
  const context = useContext(CleanupContext);
  if (!context) {
    throw new Error('useCleanupContext must be used within CleanupLayout');
  }
  return context;
};

const hasAnyRight = (authCtx, rights = []) => {
  if (!Array.isArray(rights) || rights.length === 0) {
    return true;
  }
  return rights.some(right => authCtx.checkRight(right));
};

export default function CleanupLayout({
  requiredRight = null,
  requiredRightsAny = [],
  renderActions = null,
  renderDayContent = null,
}) {
  const authCtx = useContext(AuthContext);
  const { getBranches } = useBranch();
  const { getAllEntertainmentsByBranch } = useEntertainment();
  const getBranchesRef = useRef(getBranches);
  const getAllEntertainmentsByBranchRef = useRef(getAllEntertainmentsByBranch);

  useEffect(() => {
    getBranchesRef.current = getBranches;
  });

  useEffect(() => {
    getAllEntertainmentsByBranchRef.current = getAllEntertainmentsByBranch;
  });

  const today = useMemo(() => dayjs(), []);

  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [selectedEntertainmentId, setSelectedEntertainmentId] = useState('');
  const [year, setYear] = useState(today.year());
  const [month, setMonth] = useState(today.month() + 1);
  const [selectedDate, setSelectedDate] = useState(null);

  const hasAccess = useMemo(() => {
    if (!authCtx) return false;
    if (requiredRight && !authCtx.checkRight(requiredRight)) {
      return false;
    }
    if (requiredRightsAny.length > 0 && !hasAnyRight(authCtx, requiredRightsAny)) {
      return false;
    }
    return true;
  }, [authCtx, requiredRight, requiredRightsAny]);

  useEffect(() => {
    let isActive = true;
    const fetchBranches = async () => {
      try {
        const response = await getBranchesRef.current();
        if (!isActive) return;
        const mapped = (response || []).map(branch => ({
          id: branch._id,
          label: branch.locationName || branch.name || 'Unbenannter Standort',
        }));

        setBranchOptions(mapped);
        setSelectedBranchId(prev => {
          if (prev) return prev;
          return mapped[0]?.id || '';
        });
      } catch (error) {
        if (!isActive) return;
        toast.error('Fehler beim Laden der Standorte.');
      }
    };

    fetchBranches();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const fetchEntertainments = async branchId => {
      if (!branchId) {
        if (!isActive) return;
        setEntertainmentOptions([]);
        setSelectedEntertainmentId('');
        return;
      }
      try {
        const response = await getAllEntertainmentsByBranchRef.current(branchId);
        if (!isActive) return;
        const mapped = (response || []).map(entertainment => ({
          id: entertainment._id,
          label:
            entertainment.longName ||
            entertainment.entertainmentName ||
            entertainment.name ||
            'Unbenanntes Entertainment',
        }));
        setEntertainmentOptions(mapped);
        setSelectedEntertainmentId(prev => {
          if (prev && mapped.some(item => item.id === prev)) {
            return prev;
          }
          return mapped[0]?.id || '';
        });
      } catch (error) {
        if (!isActive) return;
        setEntertainmentOptions([]);
        setSelectedEntertainmentId('');
        toast.error('Fehler beim Laden der Entertainments.');
      }
    };

    fetchEntertainments(selectedBranchId);

    return () => {
      isActive = false;
    };
  }, [selectedBranchId]);

  const handleBranchChange = useCallback(
    event => {
      setSelectedBranchId(event.target.value);
      setSelectedDate(null);
    },
    [setSelectedBranchId, setSelectedDate]
  );

  const handleEntertainmentChange = useCallback(
    event => {
      setSelectedEntertainmentId(event.target.value);
      setSelectedDate(null);
    },
    [setSelectedEntertainmentId, setSelectedDate]
  );

  const handleDateChange = useCallback(
    value => {
      setSelectedDate(value);
    },
    [setSelectedDate]
  );

  const handleNavigateMonth = useCallback(({ month: nextMonth, year: nextYear }) => {
    setMonth(nextMonth);
    setYear(nextYear);
    setSelectedDate(null);
  }, []);

  const calendarData = useMemo(() => {
    const base = dayjs(new Date(year, month - 1, 1));
    const daysInMonth = base.daysInMonth();
    return Array.from({ length: daysInMonth }, (_, idx) => ({
      day: idx + 1,
      isBookable: true,
    }));
  }, [year, month]);

  const selectedEntertainment = useMemo(
    () => entertainmentOptions.find(entertainment => entertainment.id === selectedEntertainmentId),
    [entertainmentOptions, selectedEntertainmentId]
  );

  const selectedEntertainmentName = selectedEntertainment?.label || '';
  const hasFilters = Boolean(branchOptions.length);

  const actionsContent = renderActions
    ? renderActions({
        branchOptions,
        selectedBranchId,
        entertainmentOptions,
        selectedEntertainmentId,
        selectedEntertainment,
        selectedEntertainmentName,
        selectedDate,
        year,
        month,
      })
    : null;

  const dayContent = renderDayContent
    ? renderDayContent({
        branchOptions,
        selectedBranchId,
        entertainmentOptions,
        selectedEntertainmentId,
        selectedEntertainment,
        selectedEntertainmentName,
        selectedDate,
        year,
        month,
      })
    : null;

  const contextValue = useMemo(
    () => ({
      branchOptions,
      selectedBranchId,
      setSelectedBranchId,
      entertainmentOptions,
      selectedEntertainmentId,
      setSelectedEntertainmentId,
      selectedEntertainment,
      selectedEntertainmentName,
      selectedDate,
      setSelectedDate,
      year,
      setYear,
      month,
      setMonth,
      handleDateChange,
      handleNavigateMonth,
      calendarData,
      hasFilters,
    }),
    [
      branchOptions,
      selectedBranchId,
      entertainmentOptions,
      selectedEntertainmentId,
      selectedEntertainment,
      selectedEntertainmentName,
      selectedDate,
      year,
      month,
      handleDateChange,
      handleNavigateMonth,
      calendarData,
      hasFilters,
    ]
  );

  if (!hasAccess) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <CleanupContext.Provider value={contextValue}>
      <Box
        sx={{
          ...viewRootTextPrimarySx,
          px: { xs: 2, sm: 3 },
          py: 3,
        }}
      >
        <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
          >
            <Stack spacing={2} sx={{ flex: 1, minWidth: { xs: '100%', md: 320 } }}>
              <FormControl fullWidth size='small'>
                <InputLabel id='branch-select-label'>Standort</InputLabel>
                <Select
                  labelId='branch-select-label'
                  label='Standort'
                  value={selectedBranchId}
                  onChange={handleBranchChange}
                  disabled={!hasFilters}
                >
                  {branchOptions.map(branch => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size='small' disabled={!selectedBranchId}>
                <InputLabel id='entertainment-select-label'>Entertainment</InputLabel>
                <Select
                  labelId='entertainment-select-label'
                  label='Entertainment'
                  value={selectedEntertainmentId}
                  onChange={handleEntertainmentChange}
                >
                  {entertainmentOptions.map(entertainment => (
                    <MenuItem key={entertainment.id} value={entertainment.id}>
                      {entertainment.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {actionsContent ? (
                <Stack spacing={2} alignItems='stretch'>
                  {actionsContent}
                </Stack>
              ) : null}
            </Stack>
            <Box sx={{ flex: 2, width: '100%' }}>
              <DatePicker
                data={calendarData}
                month={month}
                year={year}
                selectedDate={selectedDate}
                disabled={!selectedEntertainmentId}
                onChange={handleDateChange}
                onNavigate={handleNavigateMonth}
              />
            </Box>
          </Stack>
        </Paper>
        {dayContent}
      </Box>
    </CleanupContext.Provider>
  );
}

